import { CubeIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
  ContainerSelected,
  CustomContainer,
} from "components/Environment/shared/BuildTypes";
import { Button } from "components/Graphs/Button";
import React, { useCallback, useEffect, useId, useState } from "react";
import { allowedImageList } from "components/Environment/Create/utils";
import { validCudaVersions, validPythonVersions } from "components/Verb/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Input } from "components/Graphs/Input";
import { FlatCard } from "components/UI-lib";
import {
  buildRapidsBaseImage,
  getAvailableRapidsCudaVersions,
  getDefaultRapidsCudaVersion,
  getDefaultRapidsPythonVersion,
  getRapidsBaseImageSelectionFromImage,
  getRapidsPythonVersions,
  isRapidsBaseImage,
  parseRapidsBaseImage,
  RapidsBaseImageSelection,
  rapidsReleaseVersions,
  RapidsReleaseVersion,
} from "./rapidsBaseImages";
import { ConfigurationApplyButton } from "./ConfigurationApplyButton";

interface SingleContainerModeProps {
  selectedContainer: ContainerSelected;
  onContainerChange: (container: ContainerSelected) => void;
  pythonVersion: string;
  onPythonVersionChange: (version: string) => void;
  cudaVersion: string;
  onCudaVersionChange: (version: string) => void;
  showApplyButton?: boolean;
  onApply?: (container: ContainerSelected) => void;
  applyButtonText?: string;
  isApplyDisabled?: boolean;
  isApplyButtonDisabledInternal?: boolean;
  containerView: "featured" | "custom";
  setContainerView: (view: "featured" | "custom") => void;
  selectedGpuName?: string;
  showRapidsOption?: boolean;
  customOnly?: boolean;
  isArmInstance?: boolean;
  excludeRegistry?: boolean;
}

export const SingleContainerMode: React.FC<SingleContainerModeProps> = ({
  selectedContainer,
  onContainerChange,
  pythonVersion,
  onPythonVersionChange,
  cudaVersion,
  onCudaVersionChange,
  showApplyButton = false,
  onApply,
  containerView,
  setContainerView,
  applyButtonText = "Apply",
  isApplyDisabled = false,
  isApplyButtonDisabledInternal = false,
  selectedGpuName,
  showRapidsOption = true,
  customOnly = false,
  isArmInstance = false,
  excludeRegistry = false,
}) => {
  const activeContainerView =
    customOnly || isArmInstance ? "custom" : containerView;
  const showArmCompatibilityMessage = isArmInstance && !customOnly;

  return (
    <div className="space-y-3">
      {showApplyButton && (
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center mb-4 text-white">
            <CubeIcon className="h-5 w-5 mr-2" />
            <h4 className="text-xl text-white">Single Container</h4>
          </div>
          <ConfigurationApplyButton
            disabled={isApplyDisabled || isApplyButtonDisabledInternal}
            onClick={() => {
              if (
                containerView === "featured" &&
                selectedContainer.verbBuild?.containerUrl === "Default"
              ) {
                onCudaVersionChange(cudaVersion);
                onPythonVersionChange(pythonVersion);
              }
              onApply?.(selectedContainer);
            }}
          >
            {applyButtonText}
          </ConfigurationApplyButton>
        </div>
      )}

      {/* Toggle between Featured and Custom */}
      {!customOnly && (
        <div className="flex flex-row gap-2 mb-4">
          <Button
            variant={
              activeContainerView === "featured" ? "primary" : "secondary"
            }
            className="h-8"
            disabled={isArmInstance}
            title={
              isArmInstance
                ? "Featured containers aren't supported for Arm-based instance types."
                : undefined
            }
            onClick={() => {
              setContainerView("featured");
              // Clear custom container when switching to featured
              if (selectedContainer.customContainer) {
                onContainerChange({ verbBuild: { containerUrl: "Default" } });
              }
            }}
          >
            Featured Containers
          </Button>
          <Button
            variant={activeContainerView === "custom" ? "primary" : "secondary"}
            className="h-8"
            onClick={() => {
              setContainerView("custom");
              // Clear featured container when switching to custom
              if (selectedContainer.verbBuild) {
                onContainerChange({
                  customContainer: {
                    containerUrl: "",
                    entryPoint: "",
                  },
                });
              }
            }}
          >
            Custom Container
          </Button>
        </div>
      )}

      {/* Featured Container View */}
      {showArmCompatibilityMessage && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-100"
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-400" />
          <div>
            <p className="font-medium text-sm text-amber-200">
              Featured containers aren&apos;t available for the selected Arm-based
              instance type
            </p>
          </div>
        </div>
      )}

      {activeContainerView === "featured" && !isArmInstance && (
        <FeaturedContainer
          selectedContainer={selectedContainer}
          setSelectedContainer={onContainerChange}
          pythonVersion={pythonVersion}
          setPythonVersion={onPythonVersionChange}
          cudaVersion={cudaVersion}
          setCudaVersion={onCudaVersionChange}
          selectedGpuName={selectedGpuName}
          showRapidsOption={showRapidsOption}
        />
      )}

      {/* Custom Container View */}
      {activeContainerView === "custom" && (
        <CustomContainerSelector
          selectedContainer={selectedContainer}
          setSelectedContainer={onContainerChange}
          excludeRegistry={excludeRegistry}
        />
      )}
    </div>
  );
};

interface FeaturedContainerProps {
  selectedContainer: ContainerSelected | undefined;
  setSelectedContainer: (container: ContainerSelected) => void;
  pythonVersion: string;
  setPythonVersion: (pythonVersion: string) => void;
  cudaVersion: string;
  setCudaVersion: (cudaVersion: string) => void;
  selectedGpuName?: string;
  showRapidsOption?: boolean;
}

export const FeaturedContainer: React.FC<FeaturedContainerProps> = ({
  selectedContainer,
  setSelectedContainer,
  pythonVersion,
  setPythonVersion,
  cudaVersion,
  setCudaVersion,
  selectedGpuName,
  showRapidsOption = true,
}) => {
  const selectedImage =
    selectedContainer?.verbBuild?.containerUrl || allowedImageList[0].Image;

  const isRapidsSelected = isRapidsBaseImage(selectedImage);
  const parsedRapidsSelection = parseRapidsBaseImage(selectedImage);
  const hasUnknownRapidsTag = isRapidsSelected && !parsedRapidsSelection;

  // Persists the user's last RAPIDS choice across non-RAPIDS round trips and
  // unknown tags; a parseable URL always takes precedence when available.
  const [rememberedRapidsSelection, setRememberedRapidsSelection] =
    useState<RapidsBaseImageSelection>(() =>
      getRapidsBaseImageSelectionFromImage(selectedImage, selectedGpuName),
    );
  const candidateRapidsSelection =
    parsedRapidsSelection ?? rememberedRapidsSelection;

  const availableRapidsCudaVersions = getAvailableRapidsCudaVersions(
    candidateRapidsSelection.rapidsVersion,
    candidateRapidsSelection.pythonVersion,
    selectedGpuName,
  );

  // Reconcile a CUDA choice that the current GPU can't run.
  const rapidsSelection: RapidsBaseImageSelection =
    availableRapidsCudaVersions.includes(candidateRapidsSelection.cudaVersion)
      ? candidateRapidsSelection
      : {
          ...candidateRapidsSelection,
          cudaVersion: getDefaultRapidsCudaVersion(
            selectedGpuName,
            candidateRapidsSelection.rapidsVersion,
            candidateRapidsSelection.pythonVersion,
          ),
        };

  const featuredImages = allowedImageList.filter(
    (val) =>
      val.Image !== "None" &&
      (showRapidsOption || !isRapidsBaseImage(val.Image)),
  );
  const currentRapidsPythonVersions = getRapidsPythonVersions(
    rapidsSelection.rapidsVersion,
  );

  const applyRapidsSelection = useCallback(
    (selection: RapidsBaseImageSelection) => {
      setRememberedRapidsSelection(selection);
      setSelectedContainer({
        verbBuild: { containerUrl: buildRapidsBaseImage(selection) },
      });
    },
    [setSelectedContainer],
  );

  // Push the reconciled URL up so saved configs that drop their CUDA on this
  // GPU don't submit the stale URL on apply.
  const expectedRapidsUrl = isRapidsSelected
    ? buildRapidsBaseImage(rapidsSelection)
    : null;
  useEffect(() => {
    if (expectedRapidsUrl === null || expectedRapidsUrl === selectedImage)
      return;
    setSelectedContainer({ verbBuild: { containerUrl: expectedRapidsUrl } });
  }, [expectedRapidsUrl, selectedImage, setSelectedContainer]);

  const handleImageClick = (image: string) => {
    setSelectedContainer({ verbBuild: { containerUrl: image } });
  };

  const handleRapidsPythonVersionChange = (pythonVersion: string) => {
    const available = getAvailableRapidsCudaVersions(
      rapidsSelection.rapidsVersion,
      pythonVersion,
      selectedGpuName,
    );
    const cudaVersion = available.includes(rapidsSelection.cudaVersion)
      ? rapidsSelection.cudaVersion
      : getDefaultRapidsCudaVersion(
          selectedGpuName,
          rapidsSelection.rapidsVersion,
          pythonVersion,
        );
    applyRapidsSelection({
      rapidsVersion: rapidsSelection.rapidsVersion,
      pythonVersion,
      cudaVersion,
    });
  };

  const handleRapidsCudaVersionChange = (cudaVersion: string) => {
    applyRapidsSelection({ ...rapidsSelection, cudaVersion });
  };

  const handleRapidsReleaseChange = (rapidsVersion: RapidsReleaseVersion) => {
    const pythonVersions = getRapidsPythonVersions(rapidsVersion);
    const pythonVersion = pythonVersions.includes(rapidsSelection.pythonVersion)
      ? rapidsSelection.pythonVersion
      : getDefaultRapidsPythonVersion(rapidsVersion);
    const available = getAvailableRapidsCudaVersions(
      rapidsVersion,
      pythonVersion,
      selectedGpuName,
    );
    const cudaVersion = available.includes(rapidsSelection.cudaVersion)
      ? rapidsSelection.cudaVersion
      : getDefaultRapidsCudaVersion(
          selectedGpuName,
          rapidsVersion,
          pythonVersion,
        );
    applyRapidsSelection({ rapidsVersion, pythonVersion, cudaVersion });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div
          className={`border rounded-lg p-3 hover:border-highlight cursor-pointer bg-primaryBg transition-color duration-300 ${
            selectedImage === "Default" ? "border-highlight" : "border-zinc-800"
          }`}
          onClick={() => handleImageClick("Default")}
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-medium text-white">Brev Container</h3>
            <div className="text-xs bg-highlight text-black px-1.5 py-0.5 rounded font-medium">
              Popular
            </div>
          </div>
          <div
            className="flex flex-row flex-wrap gap-2 mt-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-[100px]">
              <div className="text-xs text-gray-400">Python Version</div>
              <Select value={pythonVersion} onValueChange={setPythonVersion}>
                <SelectTrigger className="mt-1 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {validPythonVersions.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[100px]">
              <div className="text-xs text-gray-400">Cuda Version</div>
              <Select value={cudaVersion} onValueChange={setCudaVersion}>
                <SelectTrigger className="mt-1 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {validCudaVersions.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Our simple container with Python and Cuda preinstalled
          </p>
        </div>
        {featuredImages.map((image) => {
          const isRapids = isRapidsBaseImage(image.Image);
          const isSelected = isRapids
            ? isRapidsSelected
            : selectedImage === image.Image;
          const description = isRapids
            ? `NVIDIA RAPIDS ${rapidsSelection.rapidsVersion} with Jupyter included`
            : image.Description;

          return (
            <div
              key={image.Image}
              className={`border rounded-lg p-3 hover:border-highlight cursor-pointer bg-primaryBg transition-color duration-300 ${
                isSelected ? "border-highlight" : "border-zinc-800"
              }`}
              onClick={() => {
                if (isRapids) {
                  applyRapidsSelection(rapidsSelection);
                } else {
                  handleImageClick(image.Image);
                }
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-white">
                  {isRapids ? `NVIDIA RAPIDS` : image.DisplayName}
                </h3>
                {isRapids && (
                  <div className="text-xs bg-highlight text-black px-1.5 py-0.5 rounded font-medium">
                    Best for Data Science
                  </div>
                )}
              </div>
              {isRapids && hasUnknownRapidsTag && (
                <div className="flex flex-row items-start gap-1 mt-2 text-xs text-yellow-400">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-1" />
                  <span className="break-all">
                    Saved RAPIDS configuration{" "}
                    <code className="font-mono text-yellow-300">
                      {selectedImage}
                    </code>{" "}
                    no longer available.
                  </span>
                </div>
              )}
              {isRapids && (
                <div
                  className="flex flex-row flex-wrap gap-2 mt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="w-[100px]">
                    <div className="text-xs text-gray-400">RAPIDS Release</div>
                    <Select
                      value={rapidsSelection.rapidsVersion}
                      onValueChange={(v) =>
                        handleRapidsReleaseChange(v as RapidsReleaseVersion)
                      }
                    >
                      <SelectTrigger className="mt-1 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rapidsReleaseVersions.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-[100px]">
                    <div className="text-xs text-gray-400">Python Version</div>
                    <Select
                      value={rapidsSelection.pythonVersion}
                      onValueChange={handleRapidsPythonVersionChange}
                    >
                      <SelectTrigger className="mt-1 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentRapidsPythonVersions.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-[100px]">
                    <div className="text-xs text-gray-400">CUDA Version</div>
                    <Select
                      value={rapidsSelection.cudaVersion}
                      onValueChange={handleRapidsCudaVersionChange}
                    >
                      <SelectTrigger className="mt-1 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRapidsCudaVersions.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">{description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export interface CustomContainerProps {
  selectedContainer: ContainerSelected | undefined;
  setSelectedContainer: (container: ContainerSelected) => void;
  excludeRegistry?: boolean;
}

export const CustomContainerSelector: React.FC<CustomContainerProps> = ({
  selectedContainer,
  setSelectedContainer,
  excludeRegistry = false,
}) => {
  const fieldIdPrefix = useId();
  const imageUrlId = `${fieldIdPrefix}-docker-image-url`;
  const imageUrlHelpId = `${fieldIdPrefix}-docker-image-url-help`;
  const entrypointId = `${fieldIdPrefix}-entrypoint`;
  const registryUrlId = `${fieldIdPrefix}-registry-url`;
  const registryUsernameId = `${fieldIdPrefix}-registry-username`;
  const registryPasswordId = `${fieldIdPrefix}-registry-password`;
  const labelClassName = "text-sm font-medium text-primary";

  const [imageUrl, setImageUrl] = useState(
    selectedContainer?.customContainer?.containerUrl || "",
  );
  const [entrypoint, setEntrypoint] = useState(
    selectedContainer?.customContainer?.entryPoint || "",
  );
  const [registry, setRegistry] = useState(
    selectedContainer?.customContainer?.registry?.url || "",
  );
  const [username, setUsername] = useState(
    selectedContainer?.customContainer?.registry?.username || "",
  );
  const [password, setPassword] = useState(
    selectedContainer?.customContainer?.registry?.password || "",
  );

  const handleInputChange = (
    field: "containerUrl" | "entryPoint" | "registry",
    value: string,
    registryField?: "url" | "username" | "password",
  ) => {
    let updatedContainer: CustomContainer = {
      containerUrl: imageUrl,
      entryPoint: entrypoint,
      registry:
        registry || username || password
          ? {
              url: registry || "",
              username: username || "",
              password: password || "",
            }
          : undefined,
    };

    if (field === "containerUrl") {
      updatedContainer.containerUrl = value;
      setImageUrl(value);
    } else if (field === "entryPoint") {
      updatedContainer.entryPoint = value;
      setEntrypoint(value);
    } else if (field === "registry" && registryField) {
      updatedContainer.registry = {
        url: registryField === "url" ? value : registry || "",
        username: registryField === "username" ? value : username || "",
        password: registryField === "password" ? value : password || "",
      };

      switch (registryField) {
        case "url":
          setRegistry(value);
          break;
        case "username":
          setUsername(value);
          break;
        case "password":
          setPassword(value);
          break;
      }
    }

    setSelectedContainer({
      customContainer: updatedContainer,
    });
  };

  return (
    <div className="space-y-4">
      <FlatCard className="">
        <label htmlFor={imageUrlId} className={labelClassName}>
          Docker Image URL
          <span className="ml-1 text-red-400" aria-hidden="true">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </label>
        <div className="mb-4">
          <Input
            id={imageUrlId}
            required
            aria-describedby={imageUrlHelpId}
            placeholder="nvidia/cuda"
            value={imageUrl}
            className="mt-1.5"
            onChange={(e) => handleInputChange("containerUrl", e.target.value)}
          />
          <p id={imageUrlHelpId} className="mt-1.5 text-xs text-secondary">
            Some custom containers may produce unexpected results if they
            interfere with the host system. Use with discretion.
          </p>
        </div>

        <label htmlFor={entrypointId} className={labelClassName}>
          Entrypoint Command{" "}
          <span className="font-normal text-secondary">(optional)</span>
        </label>
        <Input
          id={entrypointId}
          value={entrypoint}
          className="mt-1.5 mb-4"
          onChange={(e) => handleInputChange("entryPoint", e.target.value)}
        />
        {!excludeRegistry && (
          <fieldset className="space-y-2">
            <legend className={labelClassName}>
              Registry credentials{" "}
              <span className="font-normal text-secondary">(optional)</span>
            </legend>
            <div>
              <label htmlFor={registryUrlId} className={labelClassName}>
                Registry URL
              </label>
              <Input
                id={registryUrlId}
                placeholder="registry url"
                value={registry}
                className="mt-1.5"
                onChange={(e) =>
                  handleInputChange("registry", e.target.value, "url")
                }
              />
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <label htmlFor={registryUsernameId} className={labelClassName}>
                  Username
                </label>
                <Input
                  id={registryUsernameId}
                  placeholder="username"
                  value={username}
                  className="mt-1.5"
                  onChange={(e) =>
                    handleInputChange("registry", e.target.value, "username")
                  }
                />
              </div>
              <div>
                <label htmlFor={registryPasswordId} className={labelClassName}>
                  Password
                </label>
                <Input
                  id={registryPasswordId}
                  placeholder="password"
                  value={password}
                  type="password"
                  className="mt-1.5"
                  onChange={(e) =>
                    handleInputChange("registry", e.target.value, "password")
                  }
                />
              </div>
            </div>
          </fieldset>
        )}
      </FlatCard>
    </div>
  );
};
